import React from 'react';
import { Edit, Trash2, DollarSign, EyeOff } from 'lucide-react';

interface Bundle {
  id: string;
  title: string;
  description: string;
  full_price: number;
  discount_price?: number;
  course_ids: string[];
  is_active: boolean;
  hidden: boolean;
  thumbnail_url?: string;
  savings_percentage?: number;
  created_at: string;
  updated_at: string;
}

interface BundleCardProps {
  bundle: Bundle;
  onEdit: (bundle: Bundle) => void;
  onDelete: (bundleId: string) => void;
}

export default function BundleCard({ bundle, onEdit, onDelete }: BundleCardProps) {
  return (
    <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/20 hover:border-cyan-500/50 transition-all">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-3 mb-2">
            <h3 className="text-xl font-bold text-white">{bundle.title}</h3>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              bundle.is_active 
                ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                : 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
            }`}>
              {bundle.is_active ? 'Active' : 'Inactive'}
            </span>
            {bundle.savings_percentage && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-cyan-500/20 text-cyan-400 border border-cyan-500/30">
                Save {bundle.savings_percentage}%
              </span>
            )}
            {bundle.hidden && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-600/20 text-gray-400 border border-gray-600/30 flex items-center space-x-1">
                <EyeOff className="w-3 h-3" />
                <span>Hidden</span>
              </span>
            )}
          </div>
          <p className="text-gray-400 mb-4">{bundle.description}</p>
          <div className="flex items-center space-x-6 text-sm">
            <div className="flex items-center space-x-2">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <span className="text-white font-semibold">${bundle.full_price}</span>
              {bundle.discount_price && (
                <span className="text-green-400">→ ${bundle.discount_price}</span>
              )}
            </div>
            <span className="text-gray-400">{bundle.course_ids.length} courses</span>
          </div>
        </div>
        <div className="flex space-x-2 ml-4">
          <button
            onClick={() => onEdit(bundle)}
            className="p-2 bg-blue-600/20 hover:bg-blue-600/30 border border-blue-500/30 rounded-lg transition-colors"
            title="Edit bundle"
          >
            <Edit className="w-5 h-5 text-blue-400" />
          </button>
          <button
            onClick={() => onDelete(bundle.id)}
            className="p-2 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg transition-colors"
            title="Delete bundle"
          >
            <Trash2 className="w-5 h-5 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
